import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiSearch, FiAlertTriangle, FiX } from 'react-icons/fi';
import { fetchKpis, fetchCustomers } from '../../utils/sentimentApi';

const REFRESH_INTERVAL = 30;

const KPI_DEFS = [
  { key: 'csat_score',           label: 'CSAT Score',      icon: '⭐', format: v => v?.toFixed(1),        deltaKey: 'csat_vs_last_week',           deltaLabel: 'vs last week',  up: true,  sparkKey: 'csat_sparkline'         },
  { key: 'nps',                  label: 'NPS',             icon: '📈', format: v => v?.toFixed(0),        deltaKey: 'nps_vs_last_month',           deltaLabel: 'vs last month', up: true,  sparkKey: 'nps_sparkline'          },
  { key: 'churn_risk_pct',       label: 'Churn Risk',      icon: '⚠️', format: v => `${v?.toFixed(1)}%`, deltaKey: 'churn_risk_vs_last_month',    deltaLabel: 'vs last month', up: false, sparkKey: 'churn_risk_sparkline'   },
  { key: 'avg_response_time_min',label: 'Avg Response',    icon: '⏱️', format: v => `${v}m`,             deltaKey: 'avg_response_time_delta_min', deltaLabel: 'vs last week',  up: false, sparkKey: 'avg_response_sparkline' },
  { key: 'open_p1_alerts',       label: 'Open P1 Alerts', icon: '🔴', format: v => v,                    deltaKey: 'open_p1_new_today',           deltaLabel: 'new today',     up: false, sparkKey: 'p1_sparkline'           },
  { key: 'resolved_today',       label: 'Resolved Today', icon: '✅', format: v => v,                    deltaKey: 'resolved_vs_yesterday',       deltaLabel: 'vs yesterday',  up: true,  sparkKey: 'resolved_sparkline'     },
];

const STATUS_COLORS = {
  open:         'sm-badge-open',
  acknowledged: 'sm-badge-acknowledged',
  resolved:     'sm-badge-resolved',
  escalated:    'sm-badge-escalated',
};

const PRIORITY_COLORS = {
  P1: 'sm-badge-p1',
  P2: 'sm-badge-p2',
  P3: 'sm-badge-p3',
};

// Deterministic pseudo-random sparkline fallback (no Math.random — stable across renders)
function pseudoSpark(val, up, key) {
  if (val == null) return null;
  const seed = key.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const pts = Array.from({ length: 7 }, (_, i) => {
    const x = ((seed * (i + 1) * 9301 + 49297) % 233280) / 233280;
    const trend = up ? i * 0.016 : -(6 - i) * 0.016;
    return Math.max(0, val * (0.87 + trend + x * 0.19));
  });
  pts[6] = val;
  return pts;
}

function Sparkline({ points, up }) {
  const W = 62, H = 22, pad = 2;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const xs = points.map((_, i) => pad + (i / (points.length - 1)) * (W - pad * 2));
  const ys = points.map(v => H - pad - ((v - min) / range) * (H - pad * 2));
  const line = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${H} L${xs[0].toFixed(1)},${H} Z`;
  const color = up ? 'var(--sm-green)' : 'var(--sm-red)';
  const gradId = up ? 'spk-up' : 'spk-dn';
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="sm-kpi-spark-svg" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity=".28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="2.5" fill={color} />
    </svg>
  );
}

function KpiCard({ def, idx, val, delta, spark }) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (val == null) return;
    let rafId;
    const duration = 850 + idx * 90;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 4);
      setDisplay(val * ease);
      if (t < 1) rafId = requestAnimationFrame(tick);
      else setDisplay(val);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [val, idx]);

  const isGood = def.up ? delta >= 0 : delta <= 0;
  const formatted = val != null
    ? def.format(Number.isInteger(val) ? Math.round(display) : display)
    : '—';
  const sparkPoints = spark || pseudoSpark(val, def.up, def.key);

  return (
    <div className="sm-kpi" style={{ '--delay': `${idx * 60}ms` }}>
      <div className={`sm-kpi-icon c${idx + 1}`}>{def.icon}</div>
      <div className="sm-kpi-body">
        <div className="sm-kpi-label">{def.label}</div>
        <div className="sm-kpi-value sm-kpi-countup">{formatted}</div>
        {delta != null && (
          <div className={`sm-kpi-delta ${isGood ? 'up' : 'down'}`}>
            {delta > 0 ? '↑' : '↓'} {Math.abs(delta)} {def.deltaLabel}
          </div>
        )}
      </div>
      {sparkPoints && (
        <div className="sm-kpi-spark" style={{ '--delay': `${idx * 60 + 300}ms` }}>
          <Sparkline points={sparkPoints} up={def.up} />
        </div>
      )}
    </div>
  );
}

function CustomerDrawer({ customer: c, onClose }) {
  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);

  const scoreColor = (score) => {
    if (score <= -0.5) return 'negative';
    if (score >= 0.3) return 'positive';
    return 'neutral';
  };
  const scoreVar = (sc) =>
    sc === 'negative' ? 'var(--sm-red)' : sc === 'positive' ? 'var(--sm-green)' : 'var(--sm-amber)';
  const sc = scoreColor(c.sentiment_score);

  return (
    <>
      <div className="sm-drawer-overlay" onClick={onClose} />
      <div className="sm-drawer" role="dialog" aria-label="Customer detail">
        <div className="sm-drawer-header">
          <div>
            <div className="sm-drawer-name">{c.customer_name}</div>
            <div className="sm-drawer-id">{c.customer_id}</div>
          </div>
          <button className="sm-btn sm-btn-ghost sm-drawer-close" onClick={onClose} aria-label="Close">
            <FiX size={15} />
          </button>
        </div>

        <div className="sm-drawer-body">
          {/* Sentiment score */}
          <div className="sm-drawer-section">
            <div className="sm-drawer-section-label">Sentiment Score</div>
            <div className="sm-drawer-score-big">
              <div className="sm-drawer-score-track">
                <div
                  className={`sm-score-fill ${sc}`}
                  style={{ width: `${Math.min(100, Math.abs(c.sentiment_score || 0) * 100)}%` }}
                />
              </div>
              <span style={{ color: scoreVar(sc), fontFamily: "'Space Grotesk',sans-serif", fontSize: '1.45rem', fontWeight: 800, flexShrink: 0 }}>
                {c.sentiment_score?.toFixed(2) ?? '—'}
              </span>
            </div>
          </div>

          {/* Metrics grid */}
          <div className="sm-drawer-grid">
            <div className="sm-drawer-metric">
              <div className="sm-drawer-metric-label">Channel</div>
              <div className="sm-drawer-metric-val" style={{ textTransform: 'capitalize' }}>{c.channel || '—'}</div>
            </div>
            <div className="sm-drawer-metric">
              <div className="sm-drawer-metric-label">Emotion</div>
              <div className="sm-drawer-metric-val" style={{ textTransform: 'capitalize' }}>{c.emotion || '—'}</div>
            </div>
            <div className="sm-drawer-metric">
              <div className="sm-drawer-metric-label">Priority</div>
              <div className="sm-drawer-metric-val">
                <span className={`sm-badge ${PRIORITY_COLORS[c.priority] || ''}`}>{c.priority}</span>
              </div>
            </div>
            <div className="sm-drawer-metric">
              <div className="sm-drawer-metric-label">Status</div>
              <div className="sm-drawer-metric-val" style={{ textTransform: 'capitalize' }}>
                <span className={`sm-badge ${STATUS_COLORS[c.status] || ''}`}>{c.status}</span>
              </div>
            </div>
            <div className="sm-drawer-metric">
              <div className="sm-drawer-metric-label">Churn Signal</div>
              <div className="sm-drawer-metric-val">
                {c.churn_signal
                  ? <span className="sm-churn-chip">⚡ Risk</span>
                  : <span style={{ color: 'var(--sm-muted)', fontSize: 13 }}>Clear</span>
                }
              </div>
            </div>
            <div className="sm-drawer-metric">
              <div className="sm-drawer-metric-label">Last Contact</div>
              <div className="sm-drawer-metric-val">
                {c.days_since_contact != null ? `${c.days_since_contact}d ago` : '—'}
              </div>
            </div>
          </div>

          {c.churn_signal && (
            <div className="sm-drawer-churn-alert">
              ⚡ Churn risk detected — consider immediate outreach or escalation.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function SentimentDashboard() {
  const [kpis, setKpis]               = useState(null);
  const [customers, setCustomers]     = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [custLoading, setCustLoading] = useState(false);
  const [error, setError]             = useState('');
  const [countdown, setCountdown]     = useState(REFRESH_INTERVAL);
  const [drawerCustomer, setDrawerCustomer] = useState(null);

  const [filters, setFilters] = useState({ channel: '', region: '', priority: '', status: '', search: '' });
  const [page, setPage]       = useState(0);
  const PAGE_SIZE = 20;

  const loadKpis = useCallback(async () => {
    try {
      const data = await fetchKpis();
      setKpis(data);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    setCustLoading(true);
    try {
      const data = await fetchCustomers({ ...filters, limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      setCustomers(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setCustLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadKpis(), loadCustomers()]).finally(() => setLoading(false));
  }, [loadKpis, loadCustomers]);

  // Auto-refresh countdown
  useEffect(() => {
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          loadKpis();
          loadCustomers();
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [loadKpis, loadCustomers]);

  const handleRefresh = useCallback(() => {
    loadKpis();
    loadCustomers();
    setCountdown(REFRESH_INTERVAL);
  }, [loadKpis, loadCustomers]);

  const handleFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(0);
  };

  const scoreColor = (score) => {
    if (score <= -0.5) return 'negative';
    if (score >= 0.3)  return 'positive';
    return 'neutral';
  };

  const scoreWidth = (score) => `${Math.min(100, Math.abs(score) * 100)}%`;

  // Countdown ring
  const RING_R = 11;
  const RING_C = 2 * Math.PI * RING_R;
  const ringOffset = RING_C * (1 - countdown / REFRESH_INTERVAL);

  if (loading) return (
    <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading dashboard…</span></div>
  );

  return (
    <div>
      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {/* KPI Cards */}
      <div className="sm-card-grid">
        {KPI_DEFS.map((def, idx) => (
          <KpiCard
            key={def.key}
            def={def}
            idx={idx}
            val={kpis?.[def.key]}
            delta={kpis?.[def.deltaKey]}
            spark={kpis?.[def.sparkKey]}
          />
        ))}
      </div>

      {/* Customer Table header */}
      <div className="sm-section-header">
        <h2 className="sm-section-title">Customer Sentiment ({total})</h2>
        <div className="sm-filters">
          <div style={{ position: 'relative' }}>
            <FiSearch size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--sm-muted)', pointerEvents: 'none' }} />
            <input
              className="sm-input-search"
              style={{ paddingLeft: 28 }}
              placeholder="Search customer…"
              value={filters.search}
              onChange={e => handleFilter('search', e.target.value)}
            />
          </div>
          <select className="sm-select" value={filters.channel} onChange={e => handleFilter('channel', e.target.value)}>
            <option value="">All Channels</option>
            <option value="email">Email</option>
            <option value="chat">Chat</option>
            <option value="call">Call</option>
            <option value="social">Social</option>
            <option value="survey">Survey</option>
          </select>
          <select className="sm-select" value={filters.priority} onChange={e => handleFilter('priority', e.target.value)}>
            <option value="">All Priority</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
          </select>
          <select className="sm-select" value={filters.status} onChange={e => handleFilter('status', e.target.value)}>
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
          </select>

          {/* Auto-refresh countdown ring */}
          <div className="sm-countdown-wrap" title={`Auto-refresh in ${countdown}s`} onClick={handleRefresh}>
            <svg className="sm-countdown-ring" viewBox="0 0 28 28" aria-hidden="true">
              <circle cx={14} cy={14} r={RING_R} fill="none" stroke="var(--sm-border)" strokeWidth={2} />
              <circle
                cx={14} cy={14} r={RING_R}
                fill="none"
                stroke="var(--sm-teal-1)"
                strokeWidth={2}
                strokeDasharray={RING_C}
                strokeDashoffset={ringOffset}
                strokeLinecap="round"
                style={{ transform: 'rotate(-90deg)', transformOrigin: '14px 14px', transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
            <button className="sm-btn sm-btn-ghost sm-countdown-btn">
              <FiRefreshCw size={11} className={custLoading ? 'sm-spinner' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="sm-table-wrap">
        <table className="sm-table sm-table-clickable">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Channel</th>
              <th>Sentiment</th>
              <th>Emotion</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Churn Signal</th>
              <th>Last Contact</th>
            </tr>
          </thead>
          <tbody>
            {custLoading ? (
              [...Array(7)].map((_, i) => (
                <tr key={i} className="sm-skel-row">
                  <td><div className="sm-skel" style={{ width: '68%' }} /><div className="sm-skel sm-skel-sm" style={{ width: '42%', marginTop: 5 }} /></td>
                  <td><div className="sm-skel" style={{ width: '52%' }} /></td>
                  <td><div className="sm-skel" style={{ width: '88%' }} /></td>
                  <td><div className="sm-skel" style={{ width: '46%' }} /></td>
                  <td><div className="sm-skel" style={{ width: '32%' }} /></td>
                  <td><div className="sm-skel" style={{ width: '54%' }} /></td>
                  <td><div className="sm-skel" style={{ width: '28%' }} /></td>
                  <td><div className="sm-skel" style={{ width: '38%' }} /></td>
                </tr>
              ))
            ) : customers.length === 0 ? (
              <tr><td colSpan={8}>
                <div className="sm-empty"><div className="sm-empty-icon">🔍</div><span>No customers found.</span></div>
              </td></tr>
            ) : customers.map((c, i) => {
              const heatClass = c.churn_signal ? 'sm-row-churn' :
                c.status === 'open'      ? 'sm-row-critical'  :
                c.status === 'escalated' ? 'sm-row-at-risk'   :
                c.status === 'resolved'  ? 'sm-row-healthy'   : '';
              return (
                <tr
                  key={c.customer_id}
                  style={{ '--row-delay': `${i * 30}ms` }}
                  className={heatClass}
                  onClick={() => setDrawerCustomer(c)}
                >
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.customer_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--sm-muted)' }}>{c.customer_id}</div>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{c.channel}</td>
                  <td>
                    <div className="sm-score-bar">
                      <div className="sm-score-track">
                        <div
                          className={`sm-score-fill ${scoreColor(c.sentiment_score)}`}
                          style={{ width: scoreWidth(c.sentiment_score) }}
                        />
                      </div>
                      <span className="sm-score-num" style={{ color: `var(--sm-${scoreColor(c.sentiment_score) === 'negative' ? 'red' : scoreColor(c.sentiment_score) === 'positive' ? 'green' : 'amber'})` }}>
                        {c.sentiment_score?.toFixed(2)}
                      </span>
                    </div>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{c.emotion}</td>
                  <td><span className={`sm-badge ${PRIORITY_COLORS[c.priority] || ''}`}>{c.priority}</span></td>
                  <td><span className={`sm-badge ${STATUS_COLORS[c.status] || ''}`}>{c.status}</span></td>
                  <td>
                    {c.churn_signal
                      ? <span className="sm-churn-chip">⚡ Risk</span>
                      : <span style={{ color: 'var(--sm-muted)', fontSize: 12 }}>—</span>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)' }}>
                    {c.days_since_contact != null ? `${c.days_since_contact}d ago` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="sm-pagination">
        <span>{total} total</span>
        <button className="sm-btn sm-btn-ghost" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
        <span>Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
        <button className="sm-btn sm-btn-ghost" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>

      {drawerCustomer && (
        <CustomerDrawer customer={drawerCustomer} onClose={() => setDrawerCustomer(null)} />
      )}
    </div>
  );
}
